/**
 * useCompanyUsers — cached company-wide user list.
 *
 * Every PM template (Kanban, Sprint, Cycle, Workstream) was independently
 * calling `/companyAdmin/getAllUserByCompanyId` on mount, which for a
 * tenant with hundreds of users meant a heavy round-trip + JSON parse
 * 4× as you moved between PM pages.  This hook hoists the result into a
 * module-level cache (with a 60-second TTL) so subsequent consumers
 * read from memory instead of re-fetching.
 *
 * Returns `{ users, loading, reload }`.  Consumers can call `reload()`
 * after a known mutation (e.g. inviting a user) to bust the cache.
 *
 * The cache is keyed only by "logged in" — different tenants get
 * different responses because the API uses the JWT.  Logout / login
 * with a different account must therefore clear the cache; we expose
 * `clearCompanyUsersCache()` for the auth context to call.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import apiClient from '../services/api/config';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';

export interface CompanyUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string | null;
  profilePic?: string | null;
  departmentName?: string | null;
  role?: string | null;
}

const TTL_MS = 60_000; // 60s — generous enough to make page transitions free.

let _cache: { at: number; users: CompanyUser[] } | null = null;
let _inflight: Promise<CompanyUser[]> | null = null;
// Tiny subscriber set so multiple hook instances see fresh data after
// a reload() without having to refetch independently.
const _subs = new Set<(u: CompanyUser[]) => void>();

function normalizeAvatar(u: CompanyUser): CompanyUser {
  const raw = u.profilePic || u.avatar;
  if (!raw) return u;
  const resolved = resolveAssetUrl(raw);
  return { ...u, avatar: resolved, profilePic: resolved };
}

async function fetchUsers(): Promise<CompanyUser[]> {
  // /company/members is open to all authenticated users — try it first.
  try {
    const r = await apiClient.get('/company/members');
    if (r.data?.data?.length) return (r.data.data as CompanyUser[]).map(normalizeAvatar);
  } catch { /* fall through */ }

  // Admin-level fallbacks for older tenants / role mismatches.
  try {
    const r = await apiClient.post('/companyAdmin/getAllUserByCompanyId', {});
    return ((r.data?.data || []) as CompanyUser[]).map(normalizeAvatar);
  } catch {
    try {
      const r = await apiClient.get('/admin/users');
      return ((r.data?.data || r.data?.users || []) as CompanyUser[]).map(normalizeAvatar);
    } catch {
      return [];
    }
  }
}

function load(force = false): Promise<CompanyUser[]> {
  const now = Date.now();
  if (!force && _cache && (now - _cache.at) < TTL_MS) {
    return Promise.resolve(_cache.users);
  }
  if (_inflight) return _inflight;
  _inflight = fetchUsers()
    .then((users) => {
      _cache = { at: Date.now(), users };
      _subs.forEach((fn) => { try { fn(users); } catch { /* swallow */ } });
      return users;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function clearCompanyUsersCache() {
  _cache = null;
  _inflight = null;
}

export function useCompanyUsers() {
  const [users, setUsers] = useState<CompanyUser[]>(() => _cache?.users || []);
  const [loading, setLoading] = useState<boolean>(() => !_cache);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    let unsub: (() => void) | null = null;
    const sub = (u: CompanyUser[]) => { if (mounted.current) setUsers(u); };
    _subs.add(sub);
    unsub = () => _subs.delete(sub);

    // Kick off a fetch if cache is cold OR stale. The hook itself never
    // blocks on this — UI starts with whatever cache holds.
    const needsFetch = !_cache || (Date.now() - _cache.at) >= TTL_MS;
    if (needsFetch) {
      setLoading(true);
      load().then((u) => {
        if (mounted.current) {
          setUsers(u);
          setLoading(false);
        }
      });
    } else {
      setLoading(false);
    }

    return () => {
      mounted.current = false;
      if (unsub) unsub();
    };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    const u = await load(true);
    if (mounted.current) {
      setUsers(u);
      setLoading(false);
    }
    return u;
  }, []);

  return { users, loading, reload };
}
